/**
 * Client gateway to the `askTheo` RAG chat callable.
 *
 * Mirrors src/services/claude.client.ts — lazily binds the httpsCallable so
 * importing this module doesn't touch the Firebase functions instance at
 * load time. The heavy lifting (vector search, tool loop, Claude calls) all
 * happens server-side; this is a thin typed wrapper.
 *
 * @see functions/src/ai/askTheo.ts (server)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

export interface AskTheoCitation {
  id: number;
  title: string;
  source: string;
  sourceCollection?: string;
  sourceDocId?: string;
  date?: string;
  /** App-relative deep link (e.g. /home/deals/abc) when one exists. */
  link?: string;
}

export interface AskTheoTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AskTheoResult {
  answer: string;
  citations: AskTheoCitation[];
}

interface AskTheoInput {
  messages: AskTheoTurn[];
}

let callableRef:
  | ReturnType<typeof httpsCallable<AskTheoInput, AskTheoResult>>
  | null = null;

function getCallable() {
  if (!callableRef) {
    // Big cross-source reports (years of Theo + Pipedrive + FieldPulse) can
    // take a few minutes of tool calls + synthesis. Override the SDK's default
    // 70s client timeout so it doesn't bail before the function finishes.
    callableRef = httpsCallable<AskTheoInput, AskTheoResult>(functions, 'askTheo', {
      timeout: 300_000,
    });
  }
  return callableRef;
}

/**
 * Ask Theo a question. `messages` is the full conversation so far and MUST
 * end with the user's latest turn. Returns the assistant answer plus the
 * citations it referenced (only sources actually cited are returned).
 *
 * Throws on transport / auth errors — the callable surfaces HttpsError codes
 * (`permission-denied` for tech accounts, `resource-exhausted` for rate
 * limits, `deadline-exceeded` for timeouts).
 */
export async function askTheo(messages: AskTheoTurn[]): Promise<AskTheoResult> {
  const res = await getCallable()({ messages });
  return {
    answer: res.data?.answer ?? '',
    citations: Array.isArray(res.data?.citations) ? res.data.citations : [],
  };
}
